import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loading } from "@/components/shared";
import { ArrowLeft, Globe, ExternalLink, ShieldCheck, ShieldAlert, BadgeCheck, HelpCircle, FileText, Star, Building2, Home, MapPin, AlertTriangle } from "lucide-react";
import { cap, domainBasis, isStale, ageLabel, statusStyle, fmtDate } from "@/lib/register";

function Unconfirmed({ show }) {
  if (!show) return null;
  return (
    <span title="Not confirmed against a register" className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">unconfirmed</span>
  );
}

function LicenceCard({ lic }) {
  const st = statusStyle(lic.licence_status);
  const stale = lic.verified && isStale(lic.last_verified_at);
  const db = domainBasis(lic.domain_basis);
  return (
    <div className="card-surface p-5 sm:p-6" data-testid="licence-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-ink">
          {st.alarm ? <ShieldAlert className="h-4 w-4 text-red-600" /> : <ShieldCheck className="h-4 w-4 text-teal-400" />} {lic.jurisdiction}
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${st.cls}`}>{st.label}</span>
          {lic.business_type && <span className="rounded-full bg-[#E7F0EE] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-teal-600">{lic.business_type}</span>}
          {lic.verified ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-600"><BadgeCheck className="h-4 w-4" /> Verified against register</span>
          ) : (
            <span title="Nobody has checked this against the register. It is unconfirmed, not wrong." className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><HelpCircle className="h-4 w-4" /> Unconfirmed</span>
          )}
        </div>
      </div>

      {lic.activity_categories?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {lic.activity_categories.map((a) => (
            <span key={a} className="rounded-full border border-[#DDDAD1] px-2 py-0.5 text-[11px] text-slate-400">{cap(a)}</span>
          ))}
        </div>
      )}

      <dl className="mt-4 grid gap-4 sm:grid-cols-2">
        {lic.regulator && <div><dt className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">Regulator</dt><dd className="mt-1 text-sm text-slate-200">{lic.regulator}</dd></div>}
        {lic.licensee_name && <div><dt className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">Licensee</dt><dd className="mt-1 text-sm text-slate-200">{lic.licensee_name}</dd></div>}
        {lic.licence_number && <div><dt className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">Licence number</dt><dd className="mt-1 text-sm text-slate-200">{lic.licence_number}</dd></div>}
        {lic.licence_type && <div><dt className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">Licence type</dt><dd className="mt-1 text-sm text-slate-200">{lic.licence_type}</dd></div>}
        <div>
          <dt className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">Last verified</dt>
          <dd className={`mt-1 flex items-center gap-1.5 text-sm ${stale ? "text-amber-700" : "text-slate-200"}`}>
            {lic.last_verified_at ? `${fmtDate(lic.last_verified_at)} · ${ageLabel(lic.last_verified_at)}` : "Never"}
            {stale && <AlertTriangle className="h-3.5 w-3.5" />}
          </dd>
        </div>
      </dl>

      {stale && (
        <p className="mt-3 flex items-start gap-1.5 rounded-[8px] border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> This licence has not been re-checked against the register in over 180 days. A restated status is a claim about a regulated company that has not been re-confirmed.
        </p>
      )}

      {lic.registered_domains?.length > 0 && (
        <div className="mt-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">{db.label}</p>
          {db.note && <p className="mt-1 text-xs text-slate-500">{db.note}</p>}
          <div className="mt-2 flex flex-wrap gap-2">
            {lic.registered_domains.map((d) => (
              <a key={d} href={`https://${d}`} target="_blank" rel="noopener noreferrer" className="rounded-full border border-[#DDDAD1] px-3 py-1 text-xs text-ink transition-colors hover:border-teal-400/50">{d}</a>
            ))}
          </div>
        </div>
      )}

      {lic.source_register_url ? (
        <p className="mt-4 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
          <FileText className="h-3.5 w-3.5" /> Source:
          <a href={lic.source_register_url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">official register</a>
          {lic.source_record_ref ? <span>· {lic.source_record_ref}</span> : null}
        </p>
      ) : (
        <p className="mt-4 text-xs text-slate-500">No source register link recorded for this licence.</p>
      )}
    </div>
  );
}

export default function LicenseeDetail() {
  const { id } = useParams();
  const [op, setOp] = useState(null);

  useEffect(() => {
    api.get(`/licensees/${id}`).then(({ data }) => setOp(data)).catch(() => setOp(false));
    window.scrollTo(0, 0);
  }, [id]);

  if (op === null) return <Loading />;
  if (op === false) return <Loading label="Employer unavailable" />;

  const uf = new Set(op.verification?.unverified_fields || []);
  const careersLinks = [op.careers_url, ...(op.extra_careers_urls || [])].filter(Boolean);

  return (
    <main className="mx-auto max-w-3xl px-5 py-10 lg:px-8">
      <Link to="/jobs?tab=careers" data-testid="licensee-back" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-teal-400">
        <ArrowLeft className="h-4 w-4" /> Register
      </Link>

      <div className="card-surface p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h1 className="text-3xl font-bold leading-tight text-ink" data-testid="licensee-detail-name">{op.employer_name}</h1>
          <div className="flex flex-wrap gap-1.5">
            {(op.company_type || []).map((t) => (
              <span key={t} className="rounded-full bg-[#E7F0EE] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-teal-600">{cap(t)}</span>
            ))}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          {op.hq_location?.location && (
            <span className="inline-flex items-center gap-2 rounded-full border border-[#DDDAD1] px-4 py-2 text-sm text-slate-300"><Home className="h-4 w-4 text-teal-400" /> {op.hq_location.location}</span>
          )}
          <span className="inline-flex items-center gap-2 rounded-full border border-[#DDDAD1] px-4 py-2 text-sm text-slate-300">
            <Building2 className="h-4 w-4 text-teal-400" /> {op.remote_policy === "unknown" || !op.remote_policy ? "Remote policy unknown" : cap(op.remote_policy)}
            <Unconfirmed show={uf.has("remote_policy")} />
          </span>
        </div>

        {op.brands?.length > 0 ? (
          <div className="mt-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">Brands <Unconfirmed show={uf.has("brands")} /></p>
            <p className="mt-2 text-sm text-slate-200">{op.brands.join(", ")}</p>
          </div>
        ) : uf.has("brands") ? (
          <div className="mt-6"><p className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">Brands <Unconfirmed show /></p><p className="mt-2 text-sm text-slate-500">None recorded.</p></div>
        ) : null}

        {op.aliases?.length > 0 && (
          <div className="mt-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">Also known as</p>
            <p className="mt-2 text-sm text-slate-400">{op.aliases.join(", ")}</p>
          </div>
        )}

        {op.hiring_locations?.length > 0 && (
          <div className="mt-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">Hiring locations <Unconfirmed show={uf.has("hiring_locations")} /></p>
            <div className="mt-2 flex flex-wrap gap-2">
              {op.hiring_locations.map((l, idx) => (
                <span key={idx} className="inline-flex items-center gap-1 rounded-full border border-[#DDDAD1] px-3 py-1 text-xs text-slate-400"><MapPin className="h-3 w-3 text-teal-400" /> {l.location}</span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-[#DDDAD1] pt-6">
          {careersLinks.length > 0 ? careersLinks.map((url, i) => (
            <a key={url} href={url} target="_blank" rel="noopener noreferrer" data-testid={i === 0 ? "licensee-detail-careers" : undefined} className="btn-teal inline-flex items-center gap-1.5 text-sm">
              {i === 0 ? "Careers" : "More careers"} <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )) : (
            <span className="text-sm text-slate-500">No careers page recorded{uf.has("careers_url") ? " (unconfirmed)" : ""}.</span>
          )}
          {op.corporate_site_url && (
            <a href={op.corporate_site_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-[8px] border border-[#DDDAD1] px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-teal-400/50"><Globe className="h-4 w-4" /> Website</a>
          )}
          {op.reviews_company_id && (
            <Link to={`/company/${op.reviews_company_id}`} data-testid="licensee-detail-reviews" className="inline-flex items-center gap-1.5 rounded-[8px] border border-[#DDDAD1] px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-teal-400/50"><Star className="h-4 w-4 text-amber-500" /> Read reviews</Link>
          )}
        </div>
      </div>

      <section className="mt-8">
        <h2 className="text-lg font-bold text-ink">Licences ({(op.licences || []).length})</h2>
        {(op.licences || []).length === 0 ? (
          <div className="mt-4 card-surface p-6">
            <p className="flex items-center gap-2 text-sm text-slate-300"><MapPin className="h-4 w-4 text-slate-500" /> No gambling licences on record.</p>
            <p className="mt-2 text-xs text-slate-500">This is a valid record. The employer is based in a hiring-only location with no gambling regulator in scope, so it holds no licence in this register.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {op.licences.map((lic, i) => <LicenceCard key={i} lic={lic} />)}
          </div>
        )}
      </section>

      {op.verification && (
        <section className="mt-8 card-surface p-5 sm:p-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">Verification</p>
          <p className="mt-2 text-sm text-slate-200">Method: <span className="font-semibold">{op.verification.method}</span> · Confidence: <span className="font-semibold">{op.verification.confidence}</span></p>
          {op.verification.unverified_fields?.length > 0 && (
            <p className="mt-2 text-xs text-slate-500">Employer fields not confirmed against a register: {op.verification.unverified_fields.join(", ")}. These are marked above.</p>
          )}
        </section>
      )}

      {op.notes && (
        <section className="mt-8 card-surface p-5 sm:p-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">Analyst notes</p>
          <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-300">{op.notes}</p>
        </section>
      )}

      <p className="mt-8 text-xs leading-relaxed text-slate-500">
        This is a derived, unofficial register compiled from official sources. It is not the regulator's record. Where a licence links to a source register, that regulator's own published register is the authoritative version.
      </p>
    </main>
  );
}
